import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { PauseCircle, LifeBuoy, Users } from 'lucide-react';
import Navbar from './layout/Navbar';
import { useAuth } from '../context/AuthContext';
import './ClubPauseGate.css';

// Same address My account offers for account help.
const MARAKIN_HELP_EMAIL = 'hello@marakin.co.uk';

/**
 * ClubPauseGate — B-38 item 4 (20 Oct 2026). Wraps every /:clubSlug page.
 *
 * A suspended club is locked: Marakin staff only. The server decides who's
 * locked out (middleware/clubSuspended.js): for them GET /clubs/:slug answers
 * with a shell carrying `suspended` and `locked`, and every other club route
 * says 403 club_suspended. So:
 *   • locked  → a calm "This club is paused" page instead of the club page
 *               (and instead of the errors its requests would hit);
 *   • staff   → the page as normal, plus a small "paused" note;
 *   • anything else (active, onboarding, unknown club) → the page untouched.
 * Same ['club', slug] query every club page already runs, so no extra request.
 * If the club is paused while someone has it open, the next club request
 * that comes back 403 club_suspended refetches the club and the page swaps.
 */
export default function ClubPauseGate({ children }) {
  const { clubSlug } = useParams();
  const qc = useQueryClient();
  // Wait for the stored session first: the answer depends on WHO asks (staff
  // get the full club), and child effects run before AuthProvider's, so an
  // eager fetch would go out without the bearer token and cache the shell.
  const { loading: authLoading } = useAuth();
  const { data: club, isLoading } = useQuery({
    queryKey: ['club', clubSlug],
    queryFn: () => axios.get(`/clubs/${clubSlug}`).then(r => r.data),
    enabled: !!clubSlug && !authLoading,
  });

  useEffect(() => {
    const id = axios.interceptors.response.use(
      r => r,
      err => {
        if (err?.response?.status === 403 && err.response.data?.error === 'club_suspended') {
          qc.invalidateQueries({ queryKey: ['club', clubSlug] });
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [clubSlug, qc]);

  if (authLoading || isLoading) {
    return <div className="page-loading"><div className="spinner" style={{ width: 40, height: 40 }} /></div>;
  }
  if (club?.suspended && club?.locked) return <ClubPausedScreen club={club} />;
  return (
    <>
      {children}
      {club?.suspended && (
        <div className="club-paused-staff-note" role="status">
          <PauseCircle size={14} /> Paused: only Marakin staff can open this club.
        </div>
      )}
    </>
  );
}

function ClubPausedScreen({ club }) {
  const { user } = useAuth();
  const contact = user
    ? <Link to={`/report-problem?club=${encodeURIComponent(club.slug)}`} className="btn btn-primary"><LifeBuoy size={16} /> Contact Marakin</Link>
    : <a href={`mailto:${MARAKIN_HELP_EMAIL}`} className="btn btn-primary"><LifeBuoy size={16} /> Contact Marakin</a>;
  return (
    <div className="club-paused-page">
      {/* No club links: every one of them would only say "paused" again. */}
      <Navbar links={[]} />
      <main className="club-paused-inner">
        <div className="club-paused-card">
          <div className="club-paused-icon"><PauseCircle size={28} /></div>
          <p className="club-paused-club">{club.name}</p>
          <h1 className="club-paused-title">This club is paused</h1>
          <p className="club-paused-text">
            {club.name} isn't available on Marakin at the moment. Nothing has been deleted, and
            everything will be back as it was once the club is up and running again.
          </p>
          <p className="club-paused-text">
            If you run the club or have a question, please get in touch with the Marakin team.
          </p>
          <div className="club-paused-actions">
            {contact}
            <Link to="/my-clubs" className="btn btn-secondary"><Users size={16} /> My Clubs</Link>
          </div>
          {!user && (
            <p className="club-paused-mail">Or email us at <a href={`mailto:${MARAKIN_HELP_EMAIL}`}>{MARAKIN_HELP_EMAIL}</a>.</p>
          )}
        </div>
      </main>
    </div>
  );
}
